import { useMemo, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { Button, ToggleButton } from 'react-bootstrap';
import { MaterialReactTable } from 'material-react-table';
import { useTenants } from "../../../Hooks/Hooks";
import { Context, LoggedInUser, Tag, TenantInformation } from "../../../../types/types";
import TenantDemoCreateModal from "./TenantDemoCreateModal";
import SystemSettingsPopup from "../../DisplaysPopups/SystemSettingsPopup";
import { del, post } from "../../../../util/Axios";
import { toast } from "react-toastify";

export interface TenantsDemoViewProps {
  context: Context,
  user: LoggedInUser
}

function TenantsDemoView(props: TenantsDemoViewProps) {

  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState({})
  const [interval, setInterval] = useState(false)
  const [isOpenCreatePopup, setOpenCreatePopup] = useState(false)
  const [showSystemSettingsPopup, setShowSystemSettingsPopup] = useState(false)

  const { data: tenants, refetch, isLoading } = useTenants(props.context.id, interval)

  const columns =  useMemo(() => [
    { 
      accessorKey: 'id',
      header: 'ID',
      size: 75
    },
    {
      accessorKey: 'name',
      header: 'Name',
    },
    {
      accessorKey: 'shortname',
      header: 'Short Name',
    },
    {
      accessorKey: 'status',
      header: "Status",
    },
    {
      accessorKey: 'size',
      header: "Size",
      size: 130
    },
    {
      accessorKey: 'prevent_delete',
      header: "Deleteable?",
      size: 140,
      accessorFn: (value: any): string => {
        if (value.prevent_delete === null) return ""
        return `${!value.prevent_delete}`
      },
    },
    {
      header: 'Tags',
      accessorKey: 'tags',
        resizable: true,
        size: 110,
        accessorFn: (value: any) => {
        return value;
        },
        Cell: ({ cell }: any) => {
          const tags: Tag[] = cell.getValue().tags ?? []
        const tag_content =  tags.map((tag: Tag) => {
            return (
            <div key={`${cell.getValue().id}-tags-${tag.name}`} style={{height: '60%', borderRadius: '4px', padding: '6px', display: 'flex', alignItems: 'center', backgroundColor: `${tag.color}`}}>
                <span>{tag.name}</span>
              </div>
            )
          })
          return (
          <div key={`${cell.getValue().id}-tags`} style={{display: 'flex', gap: '4px', height: '100%', alignItems: 'center'}}>
              {tag_content}
            </div>
          )
      }
    },
    {
      accessorKey: 'contact_name',
      header: 'Contact Name',
    },
    {
      accessorKey: 'contact_email',
      header: 'Contact Email',
    },
  ], []);

  const editTenant = () => {
    const tenantId = getSelectedTenants()[0]['id']
    navigate(`/tenants/${tenantId}`)
  }

  const getSelectedTenants = () => {
    const data: TenantInformation[] = []
    Object.keys(selectedRows).forEach(id => {
      const tenant = tenants.find((t: any) => `${t.id}` === `${id}`)
      if (tenant) data.push(tenant)
    })
    return data
  }

  const handleTenantDelete = async () => {
    const selectedRows = getSelectedTenants()
    for (let i = 0; i < selectedRows.length; i++) {
      const tenant = selectedRows[i]
      const tenantId = tenant['id']
      const params = `tenantId=${tenantId}&delDb=true&delDNS=true&delSecrets=true&forceDel=false`
      let response: any = await del(`/contexts/${props.context.id}/crud/mandantendelete?${params}`).catch(error => {
        return toast.error(`Error deleting Tenant - ${error.response.status}: ${error.response.data}`)
      })
      if (response.status !== 200) {
        toast.error(`Could not delete Azure ressources of Tenant with ID ${tenantId}: Code ${response.status}. Keeping Tenant`)
        continue
      }
      toast.success(`Deletion for Tenant ${tenant['shortname']} started`)
    }
  }

  const upgradeTenant = () => {
      post(`/contexts/${props.context.id}/tenants/change-type`, {tenant_ids: Object.keys(selectedRows)}).then(response => {
        if (response.status === 200) {
          toast.success(response.data)
        } else {
          toast.error(`Failed changing Type: ${response.status} - ${response.data}`)
        }
        refetch()
      })
  }

  return (
    <div>
      <TenantDemoCreateModal
        context={props.context}
        onAbort={() => {
          setOpenCreatePopup(false)
        }}
        open={isOpenCreatePopup}
      />
      <SystemSettingsPopup
        open={showSystemSettingsPopup}
        close={() => {setShowSystemSettingsPopup(false)}}
        context={props.context}
        selectedTenant={getSelectedTenants()[0]}
      />
      <h1>{"Tenants View"}</h1>
      <div className="tenant-view-button-header">
        <Button 
         onClick={() => {setOpenCreatePopup(true)}}
        >
          Create
        </Button>
        <ToggleButton
          id="toggle-check"
          type="checkbox"
          variant="outline-primary"
          checked={interval}
          value="1"
          onChange={(e) => setInterval(e.currentTarget.checked)}
        >
          Auto Refresh
        </ToggleButton>
        <Button
          disabled={getSelectedTenants().length === 0}
          onClick={() => {handleTenantDelete()}}
        >
          Delete
        </Button>
        <Button 
          disabled={getSelectedTenants().length === 0 || getSelectedTenants().length > 1}
          onClick={() => {
            setShowSystemSettingsPopup(true)
        }}>
          Instance Settings
        </Button>
        <Button
          disabled={
            getSelectedTenants().length === 0 || getSelectedTenants().filter(t => !t.is_demo).length > 0
          }
          onClick={upgradeTenant}
        >
          Upgrade
        </Button>
        
      </div>
      <MaterialReactTable
        columns={columns}
        data={tenants ?? []}
        enableColumnResizing
        initialState={{ density: 'compact', pagination: {pageSize: 15, pageIndex: 0} }}
        onRowSelectionChange={setSelectedRows}
        enableRowSelection
        state={{ rowSelection: selectedRows, isLoading: isLoading }}
        columnResizeMode="onEnd"
        getRowId={(originalRow: any) => originalRow.id}
      />

    </div>
  )
}

export default TenantsDemoView;